import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Lightbulb, Brain, Users } from 'lucide-react';

const Services = () => {
  const services = [
    {
      title: 'Product Development',
      description: 'End-to-end digital product creation from concept to launch. We build scalable web and mobile applications that users love.',
      icon: Lightbulb,
      link: '/services/product-development',
      features: ['Web & Mobile Apps', 'API Development', 'Cloud Infrastructure', 'Quality Assurance']
    },
    {
      title: 'AI Solutions',
      description: 'Intelligent systems powered by machine learning and AI. Automate processes, gain insights, and create competitive advantages.',
      icon: Brain,
      link: '/services/ai-solutions',
      features: ['Machine Learning', 'Natural Language Processing', 'Computer Vision', 'Generative AI']
    },
    {
      title: 'Staff Augmentation',
      description: 'Scale your team with elite engineering talent on demand. Vetted senior engineers who integrate seamlessly into your workflow.',
      icon: Users,
      link: '/services/staff-augmentation',
      features: ['Senior Developers', 'Rapid Matching', 'Flexible Engagement', 'Quality Assurance']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Services"
        description="Product development, AI solutions, and staff augmentation services. We build scalable web and mobile apps, intelligent AI systems, and provide elite engineering talent on demand."
        path="/services"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden pt-32 pb-20" style={{ background: '#F8FAFC' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]" style={{ background: 'radial-gradient(circle, rgba(38,147,255,0.06) 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-widest mb-4 font-heading" style={{ color: '#2693FF' }}>
              Our Services
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 font-heading mb-8 leading-tight">
              Comprehensive digital{' '}
              <span style={{ color: '#2693FF' }}>solutions</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-500 leading-relaxed max-w-3xl">
              From product development to AI solutions and staff augmentation, we provide end-to-end services to transform your business.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={index}
                  className="card-lift bg-white rounded-2xl p-8 border border-slate-100 group flex flex-col"
                  style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                >
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ background: 'rgba(38,147,255,0.08)' }}>
                    <IconComponent className="w-7 h-7" style={{ color: '#2693FF' }} />
                  </div>

                  <h3 className="text-2xl font-bold text-slate-900 font-heading mb-4 group-hover:text-[#2693FF] transition-colors">
                    {service.title}
                  </h3>

                  <p className="text-slate-500 mb-6 leading-relaxed flex-grow">
                    {service.description}
                  </p>

                  <div className="mb-6 space-y-2.5">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#2693FF' }} />
                        <span className="text-sm text-slate-500">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={service.link}
                    className="inline-flex items-center gap-2 transition-colors group/link"
                    style={{ color: '#2693FF' }}
                  >
                    <span className="font-medium">Learn more</span>
                    <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
